import React from 'react';
import PropTypes from 'prop-types';
import graphql from "babel-plugin-relay/macro";
import {useFragment, useMutation} from "react-relay";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {toast} from "react-toastify";
import dayjs from "dayjs";
import FormModal from "../../../components/common/modals/FormModal";
import HorizontalFormSelect from "../../../components/common/horizontal-form-inputs/HorizontalFormSelect";
import HorizontalFormDate from "../../../components/common/horizontal-form-inputs/HorizontalFormDate";
import {bandedSchema} from "../../../helpers/bandedUtils";
import {createCmsSpecialtyOptions} from "../../../helpers/utils";
import {allCmsSpecialtiesDropdown} from "../../../relay/Fragments";

const EditPractitionerLocationCmsSpecialtyModal = ({showModal, onClose, selectedCmsSpecialty, allCmsSpecialties}) => {
    const mutation = graphql`
        mutation EditPractitionerLocationCmsSpecialtyModalMutation(
            $practitionerLocationCmsSpecialtyId: ID!, 
            $cmsSpecialtyId: ID!,
            $effectiveFrom: DateOnly!, 
            $effectiveTo: DateOnly!
        ) {
            practitionerLocation {
                modifyCmsSpecialty(input: {
                    practitionerLocationCmsSpecialtyId: $practitionerLocationCmsSpecialtyId
                    cmsSpecialtyId: $cmsSpecialtyId
                    effectiveFrom: $effectiveFrom
                    effectiveTo: $effectiveTo
                }) {
                    practitionerLocationCmsSpecialty {
                        id
                        cmsSpecialty {
                            applicableVersion {
                                name
                            }
                        }
                        effectiveFrom
                        effectiveTo
                    }
                }
            }
        }
    `;

    const [commitMutation, isInFlight] = useMutation(mutation);

    const cmsSpecialties = createCmsSpecialtyOptions(useFragment(allCmsSpecialtiesDropdown, allCmsSpecialties));

    const effectiveFrom = selectedCmsSpecialty?.effectiveFrom 
    ? dayjs(selectedCmsSpecialty.effectiveFrom).toDate() 
    : '';
    const effectiveTo = selectedCmsSpecialty?.effectiveTo ? dayjs(selectedCmsSpecialty.effectiveTo).toDate() : '';

    const defaultValues = {
        practitionerLocationCmsSpecialtyId: selectedCmsSpecialty?.id,
        cmsSpecialtyId: selectedCmsSpecialty?.cmsSpecialty?.id,
        effectiveFrom: effectiveFrom,
        effectiveTo: effectiveTo,
    };

    const schema = bandedSchema.shape({
        cmsSpecialtyId: yup.string().required("CMS Specialty is required"),
    });

    const methods = useForm({values: defaultValues, resolver: yupResolver(schema)});

    const handleSubmit = (data) => {
        commitMutation({
            variables: {...data},
            onCompleted: (data) => {
                if (data.practitionerLocation?.modifyCmsSpecialty?.practitionerLocationCmsSpecialty) {
                    toast.success("CMS Specialty has been updated");
                    onClose();
                }
            }
        });
    };

    return (
        <FormModal showModal={showModal}
                   onClose={onClose}
                   methods={methods}
                   isInFlight={isInFlight}
                   onSubmit={handleSubmit}
                   title={"Edit CMS Specialty"}
        >
            <HorizontalFormSelect options={cmsSpecialties} controlId={"cmsSpecialtyId"} label={"CMS Specialty"} readMode={true} />
            <HorizontalFormDate controlId={"effectiveFrom"} label={"Effective From"} />
            <HorizontalFormDate controlId={"effectiveTo"} label={"Effective To"} />
        </FormModal>
    );
};

EditPractitionerLocationCmsSpecialtyModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedCmsSpecialty: PropTypes.object.isRequired,
    allCmsSpecialties: PropTypes.array.isRequired,
};

export default EditPractitionerLocationCmsSpecialtyModal;